import { useEffect, useRef, useState } from 'react';
import { hiringApi, hiringErrorMessage } from './hiringApi';
import { statusLabel } from './hiringList';
import listStyles from './hiring.module.scss';
import styles from './phoneLookup.module.scss';

const APPLICANT_TONE = {
    NEW: 'statusNew',
    PRE_HIRE_SENT: 'statusSent',
    PRE_HIRE_COMPLETED: 'statusDone',
    HIRED: 'statusDone',
    REJECTED: 'statusRejected',
};

const DRIVER_TONE = {
    active: 'driverActive',
    inactive: 'driverInactive',
    pending: 'driverPending',
    atHome: 'driverHome',
};

const DRIVER_STATUS = {
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    atHome: 'At Home',
};

export function phoneDigits(value) {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

export function formatLookupPhone(digits) {
    if (digits.length !== 10) return digits;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function driverStatusLabel(value) {
    return DRIVER_STATUS[value] || value || '-';
}

function StatusBadge({ label, tone }) {
    return (
        <span className={styles.statusCell}>
            <span className={`${listStyles.badge} ${tone || ''}`}>{label}</span>
        </span>
    );
}

export function PhoneLookup({ canManage, canViewDrivers, onClose, onRevealApplicant, onOpenDriver, onAddApplicant }) {
    const inputRef = useRef(null);
    const requestId = useRef(0);
    const [text, setText] = useState('');
    const [result, setResult] = useState(null);
    const [searched, setSearched] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const digits = phoneDigits(text);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        if (digits.length < 2) {
            setResult(null);
            setSearched('');
            setLoading(false);
            setError('');
            return undefined;
        }
        const current = ++requestId.current;
        setLoading(true);
        const timer = window.setTimeout(() => {
            hiringApi.phoneLookup(digits).then((data) => {
                if (requestId.current !== current) return;
                setResult(data || { applicants: [], drivers: [] });
                setSearched(digits);
                setError('');
            }).catch((lookupError) => {
                if (requestId.current !== current) return;
                setError(hiringErrorMessage(lookupError));
                setResult(null);
                setSearched(digits);
            }).finally(() => {
                if (requestId.current === current) setLoading(false);
            });
        }, 250);
        return () => window.clearTimeout(timer);
    }, [digits]);

    const applicants = result?.applicants || [];
    const drivers = canViewDrivers ? (result?.drivers || []) : [];
    const settled = searched === digits && !loading;
    const showAdd = canManage && settled && digits.length >= 10 && applicants.length === 0 && drivers.length === 0;

    const openOnlyMatch = () => {
        const matches = [...applicants, ...drivers];
        if (matches.length !== 1 || searched !== digits) return;
        if (applicants.length === 1) onRevealApplicant(applicants[0]);
        else onOpenDriver(drivers[0]);
    };

    return (
        <div className={styles.backdrop} onMouseDown={onClose}>
            <div
                className={styles.panel}
                role="dialog"
                aria-label="Search by phone"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <input
                    ref={inputRef}
                    className={styles.input}
                    aria-label="Phone number"
                    placeholder="Phone number"
                    autoComplete="one-time-code"
                    inputMode="tel"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            if (showAdd) onAddApplicant(formatLookupPhone(digits));
                            else openOnlyMatch();
                        }
                    }}
                />
                <div className={styles.results}>
                    {digits.length >= 2 && loading ? <div className={styles.hint}>Searching...</div> : null}
                    {error ? <div className="errorMessage">{error}</div> : null}
                    {digits.length >= 2 ? (
                        <>
                            <section className={styles.section}>
                                <div className={styles.sectionTitle}>Applicants</div>
                                {applicants.map((row) => (
                                    <button
                                        key={row.id}
                                        type="button"
                                        className={styles.row}
                                        onClick={() => onRevealApplicant(row)}
                                    >
                                        <span>{row.name || '-'}</span>
                                        <span>{row.phoneNumber || '-'}</span>
                                        <span>{row.companyName || '-'}</span>
                                        <StatusBadge label={statusLabel(row.status)} tone={listStyles[APPLICANT_TONE[row.status]]} />
                                    </button>
                                ))}
                                {settled && applicants.length === 0 ? <div className={styles.empty}>No applicants</div> : null}
                                {result?.moreApplicants ? <div className={styles.hint}>Keep typing to narrow the list</div> : null}
                                {showAdd ? (
                                    <button
                                        type="button"
                                        className={styles.addApplicant}
                                        onClick={() => onAddApplicant(formatLookupPhone(digits))}
                                    >
                                        Add Applicant
                                    </button>
                                ) : null}
                            </section>
                            {canViewDrivers ? (
                                <section className={styles.section}>
                                    <div className={styles.sectionTitle}>Drivers</div>
                                    {drivers.map((row) => (
                                        <button
                                            key={row.id}
                                            type="button"
                                            className={styles.row}
                                            onClick={() => onOpenDriver(row)}
                                        >
                                            <span>{row.name || '-'}</span>
                                            <span>{row.phoneNumber || '-'}</span>
                                        <span>{row.companyName || '-'}</span>
                                        <StatusBadge label={driverStatusLabel(row.status)} tone={styles[DRIVER_TONE[row.status]]} />
                                        </button>
                                    ))}
                                    {settled && drivers.length === 0 ? <div className={styles.empty}>No drivers</div> : null}
                                    {result?.moreDrivers ? <div className={styles.hint}>Keep typing to narrow the list</div> : null}
                                </section>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
